import { Button, Card, Grid, Typography } from "@mui/material";
import React from "react";
import { Helmet } from "react-helmet";
import { useForm, useWatch } from "react-hook-form";
import logo from "../../images/willsoar.svg";
import { Link, useNavigate } from "react-router-dom";
import WSTextField, { TextType } from "../../Components/TextBox/WSTextBox";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import {
  callAPI,
  clearRequest,
  customValidation,
  Method,
  validations
} from "../../commonFunctions/CommonFunctions";
import WSSelectField from "../../Components/Select/WSSelect";

const Registration: React.FC<any> = React.memo(() => {
  const { register, handleSubmit, control, watch } = useForm();

  const { showMessage } = useSnackbar();
  const navigate = useNavigate();

  const selectedUserType = useWatch({ control, name: "userType" });

  const handleData = async (data: any) => {
    try {
      const request: any = await clearRequest(data);

      const response: any = await callAPI(
        "/auth/admin/register",
        Method.POST,
        request
      );

      if (response) {
        showMessage(
          response?.message,
          response.success === true ? "success" : "error"
        );
        response.success === true && navigate("/login");
      }
    } catch (error: any) {
       console.error("Failed to register", error);
        if (error.message === 'GlobalHandledError') return;
         showMessage(
        error?.response?.data.message || "An unexpected error occurred",
        "error"
      );
    }
  };
  /**
   * Method to set the request for the user
   * @param data
   * @returns
   */

  return (
    <>
      <Helmet>
        <title>Registration | Willsoar Technologies</title>
        <meta
          name="description"
          content="Start creating professional estimates and invoices. Sign up now to manage clients and streamline your workflow."
        />
      </Helmet>
      <Grid sx={{ padding: 5 }} item container>
        {/* <Grid sm={6}>
          <img
            src={logo}
            style={{ marginTop: -2 }}
            alt="logo"
            width={160}
            height={60}
          />
        </Grid> */}

        <Grid
          container
          sx={{
            height: "80vh",
            padding: 12,
            borderRadius: 5,
            justifyContent: "center",
            alignItems: "center",
            display: "flex"
          }}
        >
          <Card
            sx={{
              p: 4, // Increased padding for better spacing
              borderRadius: 3,
              width: "60%",
              boxShadow: 3,
              display: "flex",
              marginTop: -11,
              flexDirection: "column",
              alignItems: "center" // Ensures form elements are centered inside the Card
            }}
          >
            <form onSubmit={handleSubmit(handleData)} style={{ width: "100%" }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "#fe6a2c" }}
              >
                Create a new Admin
              </Typography>
              <Typography variant="body1" sx={{ mt: 2, color: "#fe6a2c" }}>
                Welcome, please enter new details.
              </Typography>

              {/* Grid container for form fields */}
              <Grid
                xs={12}
                className="modal-input"
                container
                spacing={2}
                style={{ marginTop: 10 }}
              >
                <Grid item sm={6} xs={12}>
                  <WSTextField
                    control={control}
                    id="fname"
                    {...register("fname", {
                      required: "First Name is required"
                    })}
                    name="fname"
                    rules={{ required: true }}
                    label="First Name *"
                    sx={{
                      marginBottom: 1,
                      backgroundColor: "rgba(0,0,0,.05)",
                      boxShadow: "none",
                      border: 0,
                      color: "#444",
                      fontSize: 16,
                      transition: "width 0.25s"
                    }}
                  ></WSTextField>
                </Grid>

                <Grid item sm={6} xs={12}>
                  <WSTextField
                    control={control}
                    id="lname"
                    {...register("lname")}
                    name="lname"
                    rules={{ required: false }}
                    label="Last Name "
                    sx={{
                      marginBottom: 1,
                      backgroundColor: "rgba(0,0,0,.05)",
                      boxShadow: "none",
                      border: 0,
                      color: "#444",
                      fontSize: 16,
                      transition: "width 0.25s"
                    }}
                  ></WSTextField>
                </Grid>

                <Grid item sm={6} xs={12}>
                  <WSTextField
                    control={control}
                    id="email"
                    {...register("email", { required: "Email ID is required" })}
                    name="email"
                    rules={customValidation(true, validations.email)}
                    label="Email *"
                    sx={{
                      marginBottom: 1,
                      backgroundColor: "rgba(0,0,0,.05)",
                      boxShadow: "none",
                      border: 0,
                      color: "#444",
                      fontSize: 16,
                      transition: "width 0.25s"
                    }}
                  ></WSTextField>
                </Grid>

                <Grid item sm={6} xs={12}>
                  <WSTextField
                    control={control}
                    id="address"
                    {...register("address")}
                    name="address"
                    rules={{ required: false }}
                    label="Address "
                    sx={{
                      marginBottom: 1,
                      backgroundColor: "rgba(0,0,0,.05)",
                      boxShadow: "none",
                      border: 0,
                      color: "#444",
                      fontSize: 16,
                      transition: "width 0.25s"
                    }}
                  ></WSTextField>
                </Grid>

                <Grid item xs={12}>
                  <WSTextField
                    id="jobTitleOrCompany"
                    {...register("jobTitleOrCompany", {
                      required:
                        selectedUserType === "employee"
                          ? "Job Title is required"
                          : "Company is required"
                    })}
                    rules={{ required: true }}
                    label={
                      selectedUserType === "employee" ? "Job Title" : "Company"
                    }
                    name="jobTitleOrCompany"
                    control={control}
                    sx={{
                      marginBottom: 1,
                      backgroundColor: "rgba(0,0,0,.05)",
                      boxShadow: "none",
                      border: 0,
                      color: "#444",
                      fontSize: 16,
                      transition: "width 0.25s"
                    }}
                  />
                </Grid>

                {/* Phone Number */}
                <Grid item xs={12}>
                  <WSTextField
                    control={control}
                    id="phoneNumber"
                    {...register("phoneNumber", {
                      required: "Phone Number is required"
                    })}
                    name="phoneNumber"
                    rules={customValidation(true, validations.phone)}
                    type={TextType.Number}
                    label="Phone Number"
                    sx={{
                      marginBottom: 1,
                      backgroundColor: "rgba(0,0,0,.05)",
                      boxShadow: "none",
                      border: 0,
                      color: "#444",
                      fontSize: 16,
                      transition: "width 0.25s"
                    }}
                  />
                </Grid>

                <Grid item sm={6} xs={12}>
                  <WSTextField
                    control={control}
                    id="pinCode"
                    rules={{ required: true }}
                    minLength={6}
                    maxLength={6}
                    type={TextType.AllowOnlyNumber}
                    name="pinCode"
                    label="PinCode"
                    sx={{
                      marginBottom: 1,
                      backgroundColor: "rgba(0,0,0,.05)",
                      boxShadow: "none",
                      border: 0,
                      color: "#444",
                      fontSize: 16,
                      transition: "width 0.25s"
                    }}
                  ></WSTextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <WSSelectField
                    id="userType"
                    label={"Select User Role"}
                    name="userType"
                    control={control}
                    options={[
                      { label: "Admin", value: "admin" },
                      // { label: "Employee", value: "employee" },
                      // { label: "Project Manager", value: "project_manager" }
                    ]}
                    rules={{ required: "please select a role" }}
                  />
                </Grid>

                <Grid item sm={6} xs={12}>
                  <WSTextField
                    control={control}
                    id="password"
                    {...register("password", {
                      required: true,
                      validate: (value: any) => {
                        const password = watch("confirmPassword");
                        return value === password || "Passwords do not match";
                      }
                    })}
                    name="password"
                    label="Password *"
                    type={TextType.Password}
                    sx={{
                      marginBottom: 1,
                      backgroundColor: "rgba(0,0,0,.05)",
                      boxShadow: "none",
                      border: 0,
                      color: "#444",
                      fontSize: 16,
                      transition: "width 0.25s"
                    }}
                  ></WSTextField>
                </Grid>

                <Grid item sm={6} xs={12}>
                  <WSTextField
                    control={control}
                    id="confirmPassword"
                    {...register("confirmPassword", {
                      required: true,
                      validate: (value: any) => {
                        const password = watch("password");
                        return value === password || "Passwords do not match";
                      }
                    })}
                    name="confirmPassword"
                    label="Confirm Password *"
                    type={TextType.Password}
                    sx={{
                      marginBottom: 1,
                      backgroundColor: "rgba(0,0,0,.05)",
                      boxShadow: "none",
                      border: 0,
                      color: "#444",
                      fontSize: 16,
                      transition: "width 0.25s"
                    }}
                  ></WSTextField>
                </Grid>
              </Grid>

              <Grid item xs={12} mt={2}>
                <Button
                  size="large"
                  variant="contained"
                  color="warning"
                  type="submit"
                  sx={{
                    backgroundColor: "#fe6a2c",
                    textTransform: "none",
                    color: "white",
                    width: "100%",
                    cursor: "pointer"
                  }}
                >
                  REGISTER
                </Button>
              </Grid>
              {/* <Grid item xs={12}>
                <Typography
                  variant="body1"
                  sx={{ mt: 2, color: "#fe6a2c", textAlign: "center" }}
                >
                  Have an account?{" "}
                  <Link style={{ textDecoration: "none" }} to={"/login"}>
                    Log In
                  </Link>
                </Typography>
              </Grid> */}
            </form>
          </Card>
        </Grid>
      </Grid>
    </>
  );
});

export default Registration;
